import React, { memo, useCallback, useRef } from 'react'
import { useParams } from 'react-router'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import { TtMenuItem, TtIcon, TtMenu } from '@23/react'

import { useConnectInfoQuery } from '../../../../global/blocs/useConnectInfo'
import { logMessage } from '../../../../Logging/log'
import {
  useMultipleFilesUploadWarningModal,
  usePossiblyMultipleFilesUploadWarningModal,
} from '../../MultipleFilesUploadModal/MultipleFilesUploadModal'
import {
  addBreakerCard,
  addEmptyNoteCard,
  addChyronSpeakerCard,
  addChyronSpeakersOverviewCard,
  addChyronInfoCard,
  addChyronOnCamera,
  addChyronComingUpCard,
  addPollCard,
  addEmptyImageCard,
  updateImageCard,
  addHandoutCard,
  hasTrayCard,
  addVideoCard,
  addActionCard,
} from '../../../../firebase/hooks/useTray'
import { useLocalParticipant } from '../../../../webrtc/webRTCRoom'
import { showNotice } from '../../../../Layout/Notices/Notices'
import { useCreateActionCardMutation } from '../../../../ActionCards/blocs/useActionCards'
import useFeatures from '../../../../sharedLegacy/hooks/useFeatures'
import { useVideoPicker } from '../../../../Video/components/VideoPicker/VideoPicker'
import { getVideo } from '../../../../Video/blocs/useVideo'
import { useStartUploadHandout } from '../hooks/useHandout'
import { setTrayCardEditing } from '../useTrayEditing'
import { useStartUploadSlide } from '../hooks/useSlides'

interface GutterMenuProps {
  cardId?: string
  isStandalone?: boolean
}

const GutterMenu = ({ cardId, isStandalone }: GutterMenuProps): JSX.Element => {
  const { data: webinarInfo } = useConnectInfoQuery()
  const localParticipant = useLocalParticipant()
  const { live_id: liveId } = useParams<{ live_id: string }>()
  const slideInputRef = useRef<HTMLInputElement>(null)
  const handoutInputRef = useRef<HTMLInputElement>(null)

  const pickVideo = useVideoPicker()

  const { mutateAsync: createActionCardApi } = useCreateActionCardMutation()

  const { mutateAsync: startUploadSlide } = useStartUploadSlide(liveId)
  const { mutateAsync: startUploadHandout } = useStartUploadHandout(liveId)

  const { HasWebinarActionCardsEnabled } = useFeatures()

  const getMultipleFilesUploadWarningModal =
    useMultipleFilesUploadWarningModal()

  const getPossiblyMultipleFilesUploadWarningModal =
    usePossiblyMultipleFilesUploadWarningModal()

  const handleActionCardClick = useCallback(async () => {
    try {
      const id = await createActionCardApi()
      const card = addActionCard(id, cardId)
      setTrayCardEditing(card.id)
    } catch (e) {
      console.error('Failed to create action card:', e)
      // TODO: handle error gracefully
    }
  }, [createActionCardApi, cardId])

  const handleNoteClick = () => {
    const card = addEmptyNoteCard(cardId)
    setTrayCardEditing(card.id)
  }

  const handleSlideClick = () => {
    slideInputRef.current?.click()
  }

  const uploadMultipleFilesSequentially = async (
    e: React.ChangeEvent<HTMLInputElement>,
    files: FileList
  ) => {
    if (!localParticipant) return
    const { id: participantId } = localParticipant

    const filesArray = Array.from(files)

    const fileNameIndexMap = new Map<string, string>()

    await filesArray.reduce(async (previousCardId, file) => {
      const card = await addEmptyImageCard(
        participantId,
        file.name,
        await previousCardId
      )
      if (card) {
        fileNameIndexMap.set(file.name, card.id)
        return card.id
      }
      return previousCardId
    }, Promise.resolve(cardId))

    filesArray.forEach(async (file) => {
      const id = fileNameIndexMap.get(file.name)

      if (!id) return

      let uploadedFilePages: string[] = []
      try {
        uploadedFilePages = await startUploadSlide(file)
      } catch (error) {
        showNotice({
          message: 'Failed to upload slide',
          expirationMillis: 5e3,
          isClosable: true,
        })
        return
      }

      const hasCard = await hasTrayCard(id)
      if (!hasCard) return

      const [firstImage, ...uploadedImages]: string[] = uploadedFilePages

      await updateImageCard(id, firstImage)

      void uploadedImages.reduce(async (previousCardId, image) => {
        const newCard = await addEmptyImageCard(
          participantId,
          '',
          await previousCardId
        )

        if (!newCard) return previousCardId

        await updateImageCard(newCard.id, image)
        return newCard.id
      }, Promise.resolve(id))
    })

    // eslint-disable-next-line no-param-reassign
    e.target.value = ''
  }

  const getConfirmationResult = (files: FileList): Promise<boolean> => {
    if (files.length === 1 && files[0].type === 'application/pdf') {
      return getPossiblyMultipleFilesUploadWarningModal()
    }

    if (files.length >= 2) {
      return getMultipleFilesUploadWarningModal()
    }

    return Promise.resolve(true)
  }

  const onSlideInputChangeHandler = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = e.target
    // return if no files are selected
    if (!files || files.length === 0) return

    // persist the event to prevent react from disposing of the event before we get confirmation
    e.persist()

    // if multiple files are selected, show a confirmation dialog
    // upload the files sequentially if the user confirms
    // otherwise do nothing

    const confirmation = await getConfirmationResult(files)
    if (!confirmation) return

    void uploadMultipleFilesSequentially(e, files)
  }

  const handleHandoutClick = () => {
    handoutInputRef.current?.click()
  }

  const onHandoutInputChangeHandler = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files.length >= 1) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const file = e.target.files.item(0)!
      if (file.size > 200 * 1024 * 1024) {
        showNotice({
          message: intl.formatMessage({
            defaultMessage: 'The file cannot be bigger than 200MB',
          }),
          expirationMillis: 5e3,
          isClosable: true,
        })
        return
      }
      // eslint-disable-next-line no-param-reassign
      e.target.value = ''
      const handout = await startUploadHandout(file)
      addHandoutCard(
        handout.filename,
        true,
        handout.fileSize,
        handout.iconName,
        cardId
      )
    }
  }

  const handleVideoClick = async () => {
    const videoId = await pickVideo()

    if (videoId) {
      const video = await getVideo(videoId)

      if (video.mediumDownload) {
        addVideoCard(
          videoId,
          video.title,
          video.thumbnail ?? '',
          video.mediumDownload.replace('/video_medium/', '/video_1080p/'),
          cardId
        )
      } else {
        if (video.protectedToken) {
          showNotice({
            message: intl.formatMessage({
              defaultMessage:
                "Couldn't add video for playback. This video seems to be protected.",
            }),
            expirationMillis: 5000,
            isClosable: true,
          })
        } else {
          showNotice({
            message: intl.formatMessage({
              defaultMessage: "Couldn't add video for playback.",
            }),
            expirationMillis: 5000,
            isClosable: true,
          })
          logMessage('Failed to add video to tray — Missing medium download', {
            extra: { video },
          })
        }
      }
    }
  }

  const handleChyronComingUpClick = () => {
    const card = addChyronComingUpCard(cardId)
    setTrayCardEditing(card.id)
  }

  const slot = !isStandalone ? 'gutter-menu' : ''

  const isWebinarGearMode = webinarInfo?.streaming_method === 'hardware'

  const items = (
    <>
      <TtMenuItem slot={slot} onTt-click={handleNoteClick}>
        <TtIcon slot="prefix" library="one/webinars" name="note" />
        <FormattedMessage defaultMessage="Note" />
      </TtMenuItem>
      {!isWebinarGearMode && (
        <>
          <TtMenuItem slot={slot} onTt-click={handleVideoClick}>
            <TtIcon slot="prefix" library="one/webinars" name="video" />
            <FormattedMessage defaultMessage="Video" />
          </TtMenuItem>
          <TtMenuItem slot={slot} onTt-click={handleSlideClick}>
            <input
              type="file"
              ref={slideInputRef}
              accept="image/png, image/jpeg, application/pdf"
              multiple
              onChange={onSlideInputChangeHandler}
              hidden
            />
            <TtIcon slot="prefix" library="one/webinars" name="image" />
            <FormattedMessage defaultMessage="Slide" />
          </TtMenuItem>
        </>
      )}
      <TtMenuItem slot={slot} onTt-click={() => addPollCard(cardId)}>
        <TtIcon slot="prefix" library="one/webinars" name="polls" />
        <FormattedMessage defaultMessage="Poll" />
      </TtMenuItem>
      <TtMenuItem slot={slot} onTt-click={handleHandoutClick}>
        <input
          type="file"
          ref={handoutInputRef}
          onChange={onHandoutInputChangeHandler}
          hidden
        />
        <TtIcon slot="prefix" library="one/webinars" name="handout" />
        <FormattedMessage defaultMessage="Handout" />
      </TtMenuItem>
      <TtMenuItem
        slot={slot}
        onTt-click={() => addBreakerCard('Breaker', cardId)}
      >
        <TtIcon slot="prefix" library="one/webinars" name="breaker" />
        <FormattedMessage defaultMessage="Breaker" />
      </TtMenuItem>
      {HasWebinarActionCardsEnabled && (
        <TtMenuItem slot={slot} onTt-click={handleActionCardClick}>
          <TtIcon slot="prefix" library="one/webinars" name="actioncard" />
          <FormattedMessage defaultMessage="Action Card" />
        </TtMenuItem>
      )}
      {!isWebinarGearMode && (
        <>
          <TtMenuItem slot={slot} onTt-click={handleChyronComingUpClick}>
            <TtIcon slot="prefix" library="one/global" name="chevron-right" />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {'TvCard: '}
            <FormattedMessage defaultMessage="Coming up" />
          </TtMenuItem>
          <TtMenuItem slot={slot} onClick={() => addChyronSpeakerCard(cardId)}>
            <TtIcon slot="prefix" library="one/webinars" name="speaker" />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {'TvCard: '}
            <FormattedMessage defaultMessage="Speaker" />
          </TtMenuItem>
          <TtMenuItem
            slot={slot}
            onTt-click={() => addChyronSpeakersOverviewCard(cardId)}
          >
            <TtIcon slot="prefix" library="one/webinars" name="speakers" />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {'TvCard: '}
            <FormattedMessage defaultMessage="Speakers" />
          </TtMenuItem>
          <TtMenuItem slot={slot} onTt-click={() => addChyronInfoCard(cardId)}>
            <TtIcon slot="prefix" library="one/webinars" name="info" />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {'TvCard: '}
            <FormattedMessage defaultMessage="Webinar info" />
          </TtMenuItem>
          <TtMenuItem slot={slot} onTt-click={() => addChyronOnCamera(cardId)}>
            <TtIcon slot="prefix" library="one/webinars" name="tv-card" />
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {'TvCard: '}
            <FormattedMessage defaultMessage="Go on camera" />
          </TtMenuItem>
        </>
      )}
    </>
  )

  return isStandalone ? (
    <TtMenu style={{ width: '100%' }}>{items}</TtMenu>
  ) : (
    items
  )
}

const MemoizedGutterMenu = memo(GutterMenu)

export { MemoizedGutterMenu as GutterMenu }
